import * as PopoverPrimitive from "@radix-ui/react-popover";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { IUserDetails } from "../../@types/Login";
import { IPortalPreferences } from "../../@types/Preferences";
import { ArrowDown } from "../../assets/icons/ArrowDown";
import { CircleLetter } from "../../assets/icons/CircleLetter";
import { CircleLetterOuter } from "../../assets/icons/CircleLetterOuter";
import Dummy from "../../assets/images/dummy.png";
import { AUTH_PORTAL_PREFERENCES } from "../../constants/localStorageKeys";
import { useAuth } from "../../hooks/useAuth";
import { isSuperAdminUser } from "../../utils/isSuperAdminUser";
import {
  getUserDetails,
  isSystemControllerUser,
  ROLES,
} from "../../utils/isSystemControllerUser";
import { safeStorage, safeJsonParse } from "../../utils/storage";
import {
  StyledContent,
  PopoverOption,
  IconButton,
  PopoverContainer,
  UserSettingsMobileContainer,
  ProfileInfo,
} from "./UserSettings.style";

const Popover = PopoverPrimitive.Root;

function PopoverContent({ children, ...props }: any) {
  return (
    <PopoverPrimitive.Portal>
      <StyledContent {...props}>{children}</StyledContent>
    </PopoverPrimitive.Portal>
  );
}

// Pick the avatar letter from the user's first name when available, then
// fall back to the email's first character. Returns "" when neither is set.
function getAvatarLetter(details: IUserDetails | null): string {
  const source = details?.userFirstName || details?.userEmail || "";
  return source.charAt(0).toUpperCase();
}

export function UserSettingsDesktop() {
  const { t } = useTranslation();

  const { signOut } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userFirstLetter, setUserFirstLetter] = useState("");

  const userDetails = getUserDetails();

  useEffect(() => {
    if (userDetails) {
      setUserEmail(userDetails.userEmail);
    }
    setUserFirstLetter(getAvatarLetter(userDetails));
  }, [userDetails?.userEmail, userDetails?.userFirstName]);

  const handleSignOutClick = () => {
    setIsMenuOpen((prevState) => !prevState);
    signOut();
  };

  const handleSettingsClick = () => {
    setIsMenuOpen((prevState) => !prevState);
    window.location.assign("/settings");
  };

  // EN-4961: the Reports / Insights portal toggle is for dual-portal users
  // only — identified by either the new LegacyAdmin role or the pre-EN-4961
  // legacy `is_admin` flag. Permission-based gating (admin-reports:read) is too
  // wide: SystemController has the permission but no main-portal access, and
  // Phase 2 roles (HCP / HealthcareAdmin) may inherit it without the
  // dual-portal UX. EN-5129: the reports dashboard moved /admin → /reports.
  const portalPreferencesParsed = safeJsonParse<IPortalPreferences | null>(
    safeStorage.getItem(AUTH_PORTAL_PREFERENCES),
    null
  );
  const isLegacyAdminRole = userDetails?.role === ROLES.LEGACY_ADMIN;
  const hasLegacyIsAdminFlag = portalPreferencesParsed?.isAdmin === true;
  const showReportsLink =
    (isLegacyAdminRole || hasLegacyIsAdminFlag) && !isSystemControllerUser();
  const isInReportsPortal = window.location.pathname.startsWith("/reports");
  const portalToggleLabel = isInReportsPortal
    ? t("userSettings_insightsPortal")
    : t("userSettings_reportingPortal");
  const portalToggleTarget = isInReportsPortal ? "/" : "/reports";

  const handlePortalToggleClick = () => {
    setIsMenuOpen((prevState) => !prevState);
    window.location.assign(portalToggleTarget);
  };

  // EN-5129: SuperAdmin users with a @patientmpower.com email get a link to
  // the /admin section (the in-portal Retool replacement), and a way back to
  // the regular portal while they're inside it.
  const showSuperAdminLink = isSuperAdminUser();
  const isInAdminPortal = window.location.pathname.startsWith("/admin");
  const adminToggleLabel = isInAdminPortal
    ? t("userSettings_insightsPortal")
    : t("userSettings_adminPortal");
  const adminToggleTarget = isInAdminPortal ? "/" : "/admin";

  const handleAdminToggleClick = () => {
    setIsMenuOpen((prevState) => !prevState);
    window.location.assign(adminToggleTarget);
  };

  return (
    <PopoverContainer>
      <Popover
        open={isMenuOpen}
        onOpenChange={(open: boolean) => setIsMenuOpen(open)}
      >
        <PopoverPrimitive.Trigger tabIndex={0} asChild>
          <IconButton aria-label={t("userSettings_ariaLabel")}>
            <>
              {userFirstLetter !== "" ? (
                <CircleLetter letter={userFirstLetter} />
              ) : (
                <img src={Dummy} alt={t("userSettings_profile")} />
              )}
              <ArrowDown />
            </>
          </IconButton>
        </PopoverPrimitive.Trigger>
        <PopoverContent>
          {userFirstLetter !== "" && (
            <ProfileInfo>
              <CircleLetterOuter letter={userFirstLetter} />
              <div>{userEmail}</div>
            </ProfileInfo>
          )}
          {showReportsLink && (
            <PopoverOption onClick={handlePortalToggleClick}>
              {portalToggleLabel}
            </PopoverOption>
          )}
          {showSuperAdminLink && (
            <PopoverOption onClick={handleAdminToggleClick}>
              {adminToggleLabel}
            </PopoverOption>
          )}
          <PopoverOption onClick={handleSettingsClick}>
            {t("userSettings_settings")}
          </PopoverOption>
          <PopoverOption onClick={handleSignOutClick}>
            {t("userSettings_signOut")}
          </PopoverOption>
        </PopoverContent>
      </Popover>
    </PopoverContainer>
  );
}

type IUserSettingsMobileProps = {
  onClick?: () => void;
  imgSource?: string;
};

export function MobileAvatar({
  onClick,
  imgSource = Dummy,
}: IUserSettingsMobileProps) {
  const { t } = useTranslation();

  const [userFirstLetter, setUserFirstLetter] = useState("");

  const userDetails = getUserDetails();
  useEffect(() => {
    setUserFirstLetter(getAvatarLetter(userDetails));
  }, [userDetails?.userEmail, userDetails?.userFirstName]);

  return (
    <UserSettingsMobileContainer type="button" onClick={onClick}>
      {userFirstLetter !== "" ? (
        <CircleLetter letter={userFirstLetter} />
      ) : (
        <img src={imgSource} alt={t("userSettings_profile")} />
      )}
      <p>{t("userSettings_profile")}</p>
    </UserSettingsMobileContainer>
  );
}
